import React, { useState, useEffect, Fragment } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import API from '../../services/API';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

const DEFAULT_FLOW_STEPS = [
  {
    key: 'photo',
    type: 'photo',
    label: 'Ambil Gambar',
  },
];

const STEP_TYPE_OPTIONS = [
  { value: 'scan', label: 'Imbas Pelajar' },
  { value: 'photo', label: 'Ambil Gambar' },
  { value: 'qr', label: 'Imbas QR' },
];

const FLOW_PRESETS = [
  {
    label: 'Satu Gambar Sahaja',
    value: 'single_photo',
    flow_mode: 'single_step',
    flow_steps: [{ key: 'photo', type: 'photo', label: 'Ambil Gambar' }],
  },
  {
    label: 'Imbas Sahaja',
    value: 'scan_only',
    flow_mode: 'multi_step',
    flow_steps: [{ key: 'scan', type: 'scan', label: 'Imbas Pelajar' }],
  },
  {
    label: 'Imbas → Gambar',
    value: 'scan_photo',
    flow_mode: 'multi_step',
    flow_steps: [
      { key: 'scan', type: 'scan', label: 'Imbas Pelajar' },
      { key: 'checkin_photo', type: 'photo', label: 'Gambar Daftar Masuk' },
    ],
  },
  {
    label: 'Imbas → QR',
    value: 'scan_qr',
    flow_mode: 'multi_step',
    flow_steps: [
      { key: 'scan', type: 'scan', label: 'Imbas Pelajar' },
      { key: 'event_qr', type: 'qr', label: 'Imbas QR Acara' },
    ],
  },
  {
    label: 'Imbas → QR → Gambar',
    value: 'scan_qr_photo',
    flow_mode: 'multi_step',
    flow_steps: [
      { key: 'scan', type: 'scan', label: 'Imbas Pelajar' },
      { key: 'event_qr', type: 'qr', label: 'Imbas QR Acara' },
      { key: 'proof_photo', type: 'photo', label: 'Gambar Bukti' },
    ],
  },
  {
    label: 'Imbas → 2 Gambar',
    value: 'scan_two_photos',
    flow_mode: 'multi_step',
    flow_steps: [
      { key: 'scan', type: 'scan', label: 'Imbas Pelajar' },
      { key: 'checkin_photo', type: 'photo', label: 'Gambar Daftar Masuk' },
      { key: 'verification_photo', type: 'photo', label: 'Gambar Pengesahan' },
    ],
  },
];

function safeParseFlowSteps(value) {
  if (!value) return DEFAULT_FLOW_STEPS;

  let parsed = value;

  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch (e) {
      parsed = value
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
    }
  }

  if (!Array.isArray(parsed)) return DEFAULT_FLOW_STEPS;

  return parsed
    .map((step, index) => {
      if (typeof step === 'string') {
        const type = step.trim().toLowerCase();
        return {
          key: index === 0 ? type : `${type}_${index + 1}`,
          type,
          label: type
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        };
      }

      if (typeof step === 'object' && step !== null) {
        const type = String(step.type || 'photo').toLowerCase();
        const key = String(step.key || `${type}_${index + 1}`)
          .toLowerCase()
          .replace(/\s+/g, '_');

        return {
          key,
          type,
          label:
            step.label ||
            key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          description: step.description || '',
        };
      }

      return null;
    })
    .filter((step) => step && ['scan', 'photo', 'qr'].includes(step.type));
}

function makeSafeKey(label, type, existingKeys = []) {
  const base =
    String(label || type || 'step')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'step';

  let key = base;
  let counter = 2;

  while (existingKeys.includes(key)) {
    key = `${base}_${counter}`;
    counter += 1;
  }

  return key;
}

const CustomAttendanceTypePage = () => {
  const [attendanceTypes, setAttendanceTypes] = useState([]);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [selectedType, setSelectedType] = useState(null);
  const [formTypeName, setFormTypeName] = useState('');
  const [formTypeDesc, setFormTypeDesc] = useState('');
  const [formTypeActive, setFormTypeActive] = useState(1);

  const [selectedDeleteType, setSelectedDeleteType] = useState(null);
  const [formRequiresNotif, setFormRequiresNotif] = useState(0);
  const [formNotifTime, setFormNotifTime] = useState(0);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);

  const [formFlowMode, setFormFlowMode] = useState('single_step');
  const [formFlowSteps, setFormFlowSteps] = useState(DEFAULT_FLOW_STEPS);

  const navigate = useNavigate();

  const handleView = (row) => {
    navigate(`/customAttendanceDetail?id=${row.type_id}`);
  };

  const renderFlowSummary = (row) => {
    const steps = safeParseFlowSteps(row.flow_steps);
    const isMulti = row.flow_mode === 'multi_step';

    return (
      <div style={{ minWidth: 180 }}>
        <span
          style={{
            display: 'inline-flex',
            padding: '5px 10px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 800,
            backgroundColor: isMulti ? '#dbeafe' : '#eff6ff',
            color: '#0B4C9C',
            border: '1px solid #bfdbfe',
            marginBottom: '6px',
          }}
        >
          {isMulti ? 'Beberapa Langkah' : 'Satu Gambar'}
        </span>

        <div
          style={{
            fontSize: '13px',
            color: '#334155',
            lineHeight: 1.4,
          }}
        >
          {steps.map((step) => step.label || step.key).join(' → ')}
        </div>
      </div>
    );
  };

  const columns = [
    {
      name: 'Nama',
      selector: (row) => row.name,
      sortable: true,
      width: '190px',
      wrap: true,
    },
    {
      name: 'Keterangan',
      selector: (row) => row.description || '-',
      sortable: false,
      grow: 1,
      minWidth: '240px',
      wrap: true,
    },
    {
      name: 'Aliran Kehadiran',
      cell: (row) => renderFlowSummary(row),
      grow: 1,
      minWidth: '240px',
      wrap: true,
    },
    {
      name: 'Status',
      cell: (row) => (
        <span
          style={{
            display: 'inline-flex',
            padding: '6px 11px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 800,
            backgroundColor: row.is_active === 1 ? '#dcfce7' : '#f1f5f9',
            color: row.is_active === 1 ? '#166534' : '#475569',
            border:
              row.is_active === 1 ? '1px solid #bbf7d0' : '1px solid #cbd5e1',
          }}
        >
          {row.is_active === 1 ? 'Aktif' : 'Tidak Aktif'}
        </span>
      ),
      sortable: true,
      width: '130px',
      center: true,
    },
    {
      name: 'Tindakan',
      cell: (row) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-sm"
            style={outlineBlueButtonStyle}
            onClick={() => handleView(row)}
          >
            Lihat
          </button>

          <button
            className="btn btn-sm"
            style={blueButtonStyle}
            onClick={() => openEditTypeModal(row)}
          >
            Kemaskini
          </button>

          <button
            className="btn btn-sm"
            style={dangerButtonStyle}
            onClick={() => openDeleteModal(row)}
          >
            Padam
          </button>
        </div>
      ),
      width: '350px',
      center: true,
      ignoreRowClick: true,
    },
  ];

  const fetchTeachers = () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      token: localStorage.getItem('token'),
    };

    API.get('/portal/getTeachers', { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const teacherOptions = response.data.data.map((teacher) => ({
            label: teacher.full_name,
            value: teacher.teacher_uuid,
          }));

          setTeachers(teacherOptions);
        } else {
          toast.error(response.data.message || 'Gagal mendapatkan senarai guru', {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error('Ralat semasa mendapatkan senarai guru', {
          autoClose: 5000,
        });
      });
  };

  const fetchAttendanceTypes = () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      token: localStorage.getItem('token'),
    };

    API.get('/customAttendance/type?is_portal=true', { headers })
      .then((res) => {
        if (res.data.response_code === 1000) {
          setAttendanceTypes(res.data.data || []);
        } else {
          toast.error(res.data.message || 'Gagal mendapatkan jenis kehadiran');
        }
      })
      .catch(() => toast.error('Ralat semasa mendapatkan jenis kehadiran'));
  };

  useEffect(() => {
    fetchAttendanceTypes();
  }, []);

  const resetFlow = () => {
    setFormFlowMode('single_step');
    setFormFlowSteps(DEFAULT_FLOW_STEPS);
  };

  const openAddTypeModal = () => {
    setSelectedType(null);
    setFormTypeName('');
    setFormTypeDesc('');
    setFormTypeActive(1);
    setFormRequiresNotif(0);
    setFormNotifTime(0);
    setSelectedTeachers([]);
    resetFlow();
    fetchTeachers();
    setIsTypeModalOpen(true);
  };

  const openEditTypeModal = (type) => {
    setSelectedType(type);
    setFormTypeName(type.name);
    setFormTypeDesc(type.description || '');
    setFormTypeActive(Number(type.is_active ?? 1));
    setFormRequiresNotif(Number(type.requires_notification || 0));
    setFormNotifTime(Number(type.notification_time || 0));

    setFormFlowMode(type.flow_mode || 'single_step');
    setFormFlowSteps(safeParseFlowSteps(type.flow_steps));

    fetchTeachers();

    const formattedSelected = (type.notify_teachers || []).map((t) => ({
      label: t.teacher_name || t.full_name || 'Guru Tanpa Nama',
      value: t.teacher_uuid || t.user_uuid || t.value,
    }));

    setSelectedTeachers(formattedSelected);
    setIsTypeModalOpen(true);
  };

  const closeTypeModal = () => setIsTypeModalOpen(false);

  const applyPreset = (presetValue) => {
    const preset = FLOW_PRESETS.find((x) => x.value === presetValue);
    if (!preset) return;

    setFormFlowMode(preset.flow_mode);
    setFormFlowSteps(preset.flow_steps);
  };

  const handleFlowModeChange = (value) => {
    setFormFlowMode(value);

    if (value === 'single_step') {
      setFormFlowSteps(DEFAULT_FLOW_STEPS);
    } else {
      setFormFlowSteps([
        { key: 'scan', type: 'scan', label: 'Imbas Pelajar' },
        { key: 'checkin_photo', type: 'photo', label: 'Gambar Daftar Masuk' },
      ]);
    }
  };

  const handleAddStep = () => {
    const existingKeys = formFlowSteps.map((step) => step.key);
    const key = makeSafeKey('new_photo', 'photo', existingKeys);

    setFormFlowSteps([
      ...formFlowSteps,
      {
        key,
        type: 'photo',
        label: 'Langkah Gambar Baharu',
      },
    ]);
  };

  const handleRemoveStep = (index) => {
    if (formFlowMode === 'single_step') {
      toast.warning('Aliran satu langkah mesti kekalkan langkah gambar.');
      return;
    }

    const next = formFlowSteps.filter((_, i) => i !== index);

    if (next.length === 0) {
      toast.error('Aliran mesti mempunyai sekurang-kurangnya satu langkah.');
      return;
    }

    setFormFlowSteps(next);
  };

  const updateFlowStep = (index, patch) => {
    setFormFlowSteps((prev) => {
      const next = [...prev];
      const current = { ...next[index], ...patch };

      if (patch.label && !patch.key) {
        const otherKeys = next
          .filter((_, i) => i !== index)
          .map((step) => step.key);

        current.key = makeSafeKey(patch.label, current.type, otherKeys);
      }

      if (patch.type && !current.label) {
        current.label = patch.type
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
      }

      next[index] = current;
      return next;
    });
  };

  const validateFlow = () => {
    if (formFlowMode === 'single_step') {
      return {
        ok: true,
        steps: DEFAULT_FLOW_STEPS,
      };
    }

    if (!Array.isArray(formFlowSteps) || formFlowSteps.length === 0) {
      return {
        ok: false,
        message: 'Sila tambah sekurang-kurangnya satu langkah aliran.',
      };
    }

    const keys = new Set();

    for (const step of formFlowSteps) {
      if (!step.key || !step.type) {
        return {
          ok: false,
          message: 'Setiap langkah mesti ada key dan jenis langkah.',
        };
      }

      if (!['scan', 'photo', 'qr'].includes(step.type)) {
        return {
          ok: false,
          message: `Jenis langkah tidak sah: ${step.type}`,
        };
      }

      if (keys.has(step.key)) {
        return {
          ok: false,
          message: `Step key berulang: ${step.key}`,
        };
      }

      keys.add(step.key);
    }

    return {
      ok: true,
      steps: formFlowSteps.map((step) => ({
        key: step.key,
        type: step.type,
        label:
          step.label ||
          step.key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        ...(step.description ? { description: step.description } : {}),
      })),
    };
  };

  const handleSaveType = () => {
    if (!formTypeName) {
      toast.error('Nama tidak boleh kosong');
      return;
    }

    const flowValidation = validateFlow();

    if (!flowValidation.ok) {
      toast.error(flowValidation.message);
      return;
    }

    const headers = { token: localStorage.getItem('token') };

    const payload = {
      name: formTypeName,
      description: formTypeDesc,
      is_active: formTypeActive,
      requires_notification: formRequiresNotif,
      notification_time: formNotifTime,
      notify_teachers: selectedTeachers.map((t) => t.value),
      flow_mode: formFlowMode,
      flow_steps: flowValidation.steps,
    };

    if (selectedType) {
      API.put(
        `/customAttendance/type/${selectedType.type_id}?is_portal=true`,
        payload,
        { headers }
      )
        .then((res) => {
          if (res.data.response_code === 1000) {
            toast.success('Jenis kehadiran berjaya dikemaskini');
            closeTypeModal();
            fetchAttendanceTypes();
          } else {
            toast.error(res.data.message || 'Gagal mengemaskini jenis kehadiran');
          }
        })
        .catch(() => toast.error('Ralat semasa mengemaskini jenis kehadiran'));
    } else {
      API.post(`/customAttendance/type?is_portal=true`, payload, { headers })
        .then((res) => {
          if (res.data.response_code === 1000) {
            toast.success('Jenis kehadiran berjaya ditambah');
            closeTypeModal();
            fetchAttendanceTypes();
          } else {
            toast.error(res.data.message || 'Gagal menambah jenis kehadiran');
          }
        })
        .catch(() => toast.error('Ralat semasa menambah jenis kehadiran'));
    }
  };

  const openDeleteModal = (type) => {
    setSelectedDeleteType(type);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => setIsDeleteModalOpen(false);

  const handleDeleteType = () => {
    const headers = { token: localStorage.getItem('token') };

    API.delete(
      `/customAttendance/type/${selectedDeleteType.type_id}?is_portal=true`,
      { headers }
    )
      .then((res) => {
        if (res.data.response_code === 1000) {
          toast.success('Jenis kehadiran berjaya dipadam');
          closeDeleteModal();
          fetchAttendanceTypes();
        } else {
          toast.error(res.data.message || 'Gagal memadam jenis kehadiran');
        }
      })
      .catch(() => toast.error('Ralat semasa memadam jenis kehadiran'));
  };

  return (
    <Fragment>
      <div
        className="container-fluid"
        style={{
          background: 'linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)',
          minHeight: '100vh',
          paddingTop: '24px',
          paddingBottom: '40px',
        }}
      >
        <div
          className="card rounded-25"
          style={{
            border: '1px solid #dbeafe',
            borderRadius: '22px',
            boxShadow: '0 18px 45px rgba(11, 76, 156, 0.10)',
            overflow: 'hidden',
          }}
        >
          <div
            className="card-header"
            style={{
              background:
                'linear-gradient(135deg, #0B4C9C 0%, #0F65C9 55%, #1E88E5 100%)',
              borderBottom: 'none',
              padding: '22px 26px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  color: '#bfdbfe',
                  fontSize: '12px',
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  marginBottom: '6px',
                }}
              >
                Kehadiran Khas
              </div>

              <h5
                style={{
                  color: '#ffffff',
                  margin: 0,
                  fontWeight: 800,
                }}
              >
                Jenis Kehadiran
              </h5>

              <p
                style={{
                  color: '#dbeafe',
                  margin: '6px 0 0',
                  fontSize: '14px',
                }}
              >
                Tetapkan jenis kehadiran, aliran daftar masuk, notifikasi dan
                guru yang perlu dimaklumkan.
              </p>
            </div>

            <button
              className="btn"
              style={{
                backgroundColor: '#ffffff',
                color: '#0B4C9C',
                fontSize: '14px',
                fontWeight: 800,
                padding: '11px 18px',
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 12px 25px rgba(15, 23, 42, 0.18)',
              }}
              onClick={openAddTypeModal}
            >
              Tambah Jenis Kehadiran
            </button>
          </div>

          <div
            className="card-body"
            style={{
              backgroundColor: '#ffffff',
              padding: '22px',
            }}
          >
            <DataTable
              columns={columns}
              data={attendanceTypes}
              striped={false}
              persistTableHead
              highlightOnHover
              customStyles={blueDataTableStyles}
              noDataComponent={
                <div
                  style={{
                    padding: '36px 16px',
                    color: '#64748b',
                    fontWeight: 600,
                  }}
                >
                  Tiada jenis kehadiran dijumpai. Klik “Tambah Jenis Kehadiran”
                  untuk cipta yang baharu.
                </div>
              }
            />
          </div>
        </div>
      </div>

      <Modal isOpen={isTypeModalOpen} toggle={closeTypeModal} size="lg">
        <ModalHeader toggle={closeTypeModal} style={modalHeaderStyle}>
          {selectedType ? 'Kemaskini Jenis Kehadiran' : 'Tambah Jenis Kehadiran'}
        </ModalHeader>

        <ModalBody>
          <div className="form-group">
            <label style={labelStyle}>Nama</label>
            <input
              className="form-control"
              value={formTypeName}
              onChange={(e) => setFormTypeName(e.target.value)}
              style={inputStyle}
              placeholder="Contoh: Aktiviti Kokurikulum"
            />
          </div>

          <div className="form-group mt-2">
            <label style={labelStyle}>Keterangan</label>
            <textarea
              className="form-control"
              rows="3"
              value={formTypeDesc}
              onChange={(e) => setFormTypeDesc(e.target.value)}
              style={inputStyle}
              placeholder="Terangkan tujuan jenis kehadiran ini"
            />
          </div>

          <div className="form-group mt-2">
            <label style={labelStyle}>Status</label>
            <select
              className="form-control"
              value={formTypeActive}
              onChange={(e) => setFormTypeActive(Number(e.target.value))}
              style={inputStyle}
            >
              <option value={1}>Aktif</option>
              <option value={0}>Tidak Aktif</option>
            </select>
          </div>

          <hr />

          <div className="form-group mt-3">
            <label className="fw-bold" style={labelStyle}>
              Aliran Kehadiran
            </label>

            <select
              className="form-control"
              value={formFlowMode}
              onChange={(e) => handleFlowModeChange(e.target.value)}
              style={inputStyle}
            >
              <option value="single_step">
                Satu Langkah - Wajib Ambil Gambar
              </option>
              <option value="multi_step">
                Beberapa Langkah - Aliran Fleksibel
              </option>
            </select>

            <small className="text-muted">
              Kehadiran biasa boleh guna “Satu Langkah”. Guna “Beberapa Langkah”
              untuk aliran seperti imbas pelajar → QR → gambar.
            </small>
          </div>

          <div className="form-group mt-3">
            <label style={labelStyle}>Template Aliran</label>
            <select
              className="form-control"
              onChange={(e) => applyPreset(e.target.value)}
              defaultValue=""
              style={inputStyle}
            >
              <option value="" disabled>
                Pilih template...
              </option>
              {FLOW_PRESETS.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-3">
            <div className="d-flex align-items-center justify-content-between">
              <label className="fw-bold mb-0" style={labelStyle}>
                Langkah Aliran
              </label>

              <button
                type="button"
                className="btn btn-sm"
                style={outlineBlueButtonStyle}
                onClick={handleAddStep}
                disabled={formFlowMode === 'single_step'}
              >
                Tambah Langkah
              </button>
            </div>

            <div className="mt-2">
              {formFlowSteps.map((step, index) => (
                <div
                  key={`${step.key}-${index}`}
                  className="border rounded p-3 mb-2"
                  style={{
                    background: '#f8fbff',
                    borderColor: '#bfdbfe',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <strong style={{ color: '#0B4C9C' }}>
                      Langkah {index + 1}
                    </strong>

                    <button
                      type="button"
                      className="btn btn-sm"
                      style={dangerOutlineButtonStyle}
                      onClick={() => handleRemoveStep(index)}
                      disabled={formFlowMode === 'single_step'}
                    >
                      Buang
                    </button>
                  </div>

                  <div className="row">
                    <div className="col-md-4">
                      <label style={labelStyle}>Jenis</label>
                      <select
                        className="form-control"
                        value={step.type}
                        disabled={formFlowMode === 'single_step'}
                        onChange={(e) => {
                          const newType = e.target.value;
                          const otherKeys = formFlowSteps
                            .filter((_, i) => i !== index)
                            .map((s) => s.key);

                          updateFlowStep(index, {
                            type: newType,
                            key: makeSafeKey(
                              step.label || newType,
                              newType,
                              otherKeys
                            ),
                          });
                        }}
                        style={inputStyle}
                      >
                        {STEP_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label style={labelStyle}>Label</label>
                      <input
                        className="form-control"
                        value={step.label || ''}
                        disabled={formFlowMode === 'single_step'}
                        onChange={(e) =>
                          updateFlowStep(index, { label: e.target.value })
                        }
                        placeholder="Contoh: Gambar Daftar Masuk"
                        style={inputStyle}
                      />
                    </div>

                    <div className="col-md-4">
                      <label style={labelStyle}>Step Key</label>
                      <input
                        className="form-control"
                        value={step.key || ''}
                        disabled={formFlowMode === 'single_step'}
                        onChange={(e) =>
                          updateFlowStep(index, {
                            key: e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, '_')
                              .replace(/^_+|_+$/g, ''),
                          })
                        }
                        placeholder="Contoh: checkin_photo"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div className="mt-2">
                    <label style={labelStyle}>Keterangan</label>
                    <input
                      className="form-control"
                      value={step.description || ''}
                      disabled={formFlowMode === 'single_step'}
                      onChange={(e) =>
                        updateFlowStep(index, { description: e.target.value })
                      }
                      placeholder="Keterangan ringkas untuk paparan aplikasi"
                      style={inputStyle}
                    />
                  </div>
                </div>
              ))}
            </div>

            <small className="text-muted">
              Step key mesti unik. Contoh untuk dua gambar:{' '}
              <strong>checkin_photo</strong> dan{' '}
              <strong>verification_photo</strong>.
            </small>
          </div>

          <hr />

          <div className="form-group mt-3">
            <label style={labelStyle}>Perlu Hantar Notifikasi</label>
            <select
              className="form-control"
              value={formRequiresNotif}
              onChange={(e) => setFormRequiresNotif(Number(e.target.value))}
              style={inputStyle}
            >
              <option value={1}>Ya</option>
              <option value={0}>Tidak</option>
            </select>
          </div>

          {formRequiresNotif === 1 && (
            <>
              <div className="form-group mt-3">
                <label style={labelStyle}>
                  Masa Notifikasi (saat)
                  <span
                    className="text-muted ms-1"
                    style={{ fontSize: '0.9em' }}
                  >
                    — tempoh selepas daftar masuk sebelum notifikasi dihantar
                  </span>
                </label>

                <input
                  type="number"
                  className="form-control"
                  value={formNotifTime}
                  min="0"
                  onChange={(e) => setFormNotifTime(Number(e.target.value))}
                  placeholder="Contoh: 300 = 5 minit"
                  style={inputStyle}
                />

                {formNotifTime > 0 && (
                  <small className="text-md text-black">
                    ⏱ Anggaran {(formNotifTime / 60).toFixed(1)} minit selepas
                    daftar masuk.
                  </small>
                )}

                {formNotifTime === 0 && (
                  <small className="text-muted">
                    Jika tetapan <strong>0</strong>, notifikasi akan dihantar
                    serta-merta selepas daftar masuk.
                  </small>
                )}
              </div>

              <div className="form-group mt-3">
                <label style={labelStyle}>Guru Untuk Dimaklumkan</label>

                <Select
                  isMulti
                  options={teachers}
                  value={selectedTeachers}
                  onChange={(selected) => setSelectedTeachers(selected || [])}
                  placeholder="Pilih guru yang perlu dimaklumkan..."
                  classNamePrefix="react-select"
                />

                <small className="text-muted">
                  Pilih satu atau lebih guru yang akan menerima notifikasi.
                </small>
              </div>
            </>
          )}
        </ModalBody>

        <ModalFooter style={modalFooterStyle}>
          <Button style={primaryButtonStyle} onClick={handleSaveType}>
            {selectedType ? 'Kemaskini' : 'Tambah'}
          </Button>

          <Button style={secondaryButtonStyle} onClick={closeTypeModal}>
            Batal
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isDeleteModalOpen} toggle={closeDeleteModal}>
        <ModalHeader
          toggle={closeDeleteModal}
          style={{
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            borderBottom: '1px solid #fecaca',
            fontWeight: 800,
          }}
        >
          Sahkan Padam
        </ModalHeader>

        <ModalBody>
          Padam jenis kehadiran <strong>{selectedDeleteType?.name}</strong>?
        </ModalBody>

        <ModalFooter style={modalFooterStyle}>
          <Button style={deleteButtonStyle} onClick={handleDeleteType}>
            Ya, Padam
          </Button>

          <Button style={secondaryButtonStyle} onClick={closeDeleteModal}>
            Batal
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default CustomAttendanceTypePage;

const blueDataTableStyles = {
  table: {
    style: {
      borderRadius: '18px',
      overflow: 'hidden',
    },
  },
  headRow: {
    style: {
      backgroundColor: '#eff6ff',
      borderBottomColor: '#bfdbfe',
      minHeight: '56px',
    },
  },
  headCells: {
    style: {
      color: '#0B4C9C',
      fontSize: '12px',
      fontWeight: 900,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    },
  },
  rows: {
    style: {
      minHeight: '64px',
      borderBottomColor: '#e5eefb',
      fontSize: '14px',
      color: '#0f172a',
    },
    highlightOnHoverStyle: {
      backgroundColor: '#eff6ff',
    },
  },
};

const modalHeaderStyle = {
  backgroundColor: '#eff6ff',
  color: '#0B4C9C',
  borderBottom: '1px solid #bfdbfe',
  fontWeight: 800,
};

const modalFooterStyle = {
  borderTop: '1px solid #e2e8f0',
  backgroundColor: '#f8fafc',
};

const labelStyle = {
  fontWeight: 700,
  color: '#0f172a',
  marginBottom: '6px',
};

const inputStyle = {
  borderRadius: '12px',
  borderColor: '#bfdbfe',
  padding: '10px 12px',
};

const primaryButtonStyle = {
  backgroundColor: '#0B4C9C',
  color: '#ffffff',
  fontWeight: 800,
  border: 'none',
  borderRadius: '10px',
  padding: '9px 16px',
};

const secondaryButtonStyle = {
  backgroundColor: '#e2e8f0',
  color: '#334155',
  fontWeight: 800,
  border: 'none',
  borderRadius: '10px',
  padding: '9px 16px',
};

const blueButtonStyle = {
  backgroundColor: '#0B4C9C',
  color: '#ffffff',
  border: 'none',
  borderRadius: '10px',
  fontWeight: 800,
  padding: '7px 13px',
  fontSize: '12px',
};

const outlineBlueButtonStyle = {
  backgroundColor: '#ffffff',
  color: '#0B4C9C',
  border: '1px solid #0B4C9C',
  borderRadius: '10px',
  fontWeight: 800,
  padding: '7px 13px',
  fontSize: '12px',
};

const dangerButtonStyle = {
  backgroundColor: '#dc2626',
  color: '#ffffff',
  border: 'none',
  borderRadius: '10px',
  fontWeight: 800,
  padding: '7px 13px',
  fontSize: '12px',
};

const dangerOutlineButtonStyle = {
  backgroundColor: '#ffffff',
  color: '#dc2626',
  border: '1px solid #dc2626',
  borderRadius: '10px',
  fontWeight: 800,
  padding: '7px 13px',
  fontSize: '12px',
};

const deleteButtonStyle = {
  backgroundColor: '#dc2626',
  color: '#ffffff',
  fontWeight: 800,
  border: 'none',
  borderRadius: '10px',
  padding: '9px 16px',
};